import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from './layouts/DashboardLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { authService } from './services/auth.service';
import { csrfService } from './services/csrf.service';
import { 
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  AcceptInvitePage,
  BulkWinesPage,
  BulkWineEditPage,
  BulkWineDetailPage,
  CleanSkinPage,
  FruitPage,
  SuppliersPage,
  SupplierDetailPage,
  SupplierEditPage,
  UsersPage,
  UserEditPage,
  UserDetailPage,
  ProfilePage,
  NotFoundPage
} from './pages';

interface ProtectedRouteProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, isAuthenticated }) => {
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = authService.getToken();
    const isAuth = !!token;
    setIsAuthenticated(isAuth);
    
    // Fetch CSRF token if user is authenticated - 
    if (isAuth) {
      csrfService.fetchToken().catch((err) => {
        console.error('Failed to fetch CSRF token on app start:', err);
        // Don't block app load if CSRF fetch fails
      });
    }
    
    setIsLoading(false);
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          
          {/* All protected routes wrapped in DashboardLayout */}
          <Route
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Navigate to="/inventory/bulk-wine" replace />} />
            <Route path="/inventory/bulk-wine" element={<BulkWinesPage />} />
            <Route path="/bulk-wines/new" element={<BulkWineEditPage />} />
            <Route path="/bulk-wines/:id" element={<BulkWineDetailPage />} />
            <Route path="/bulk-wines/:id/edit" element={<BulkWineEditPage />} />
            <Route path="/inventory/cleanskin" element={<CleanSkinPage />} />
            <Route path="/inventory/fruit" element={<FruitPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/suppliers/new" element={<SupplierEditPage />} />
            <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
            <Route path="/suppliers/:id/edit" element={<SupplierEditPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/users/new" element={<UserEditPage />} />
            <Route path="/users/:id" element={<UserDetailPage />} />
            <Route path="/users/:id/edit" element={<UserEditPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
          
          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
